import { BaseDomainError, BaseApplicationError, BaseInfrastructureError } from '@swoft/core';

/**
 * Domain errors for Domain Designer operations
 * Following @swoft/core-errors structured error patterns
 * 
 * These errors represent pure domain concerns following Eric Evans DDD principles.
 * Infrastructure concerns (like MCP) should NOT be referenced here.
 */

export class DomainDesignerError extends BaseDomainError {
  readonly code: string;
  
  constructor(message: string, code: string) {
    super(message, {
      ux: ['An error occurred in the domain designer'],
      dx: [`Error code: ${code}`, 'Check error details for more information']
    });
    this.code = code;
  }
}

export class DomainNotFoundError extends BaseDomainError {
  readonly code = 'DOMAIN_NOT_FOUND';
  
  constructor(domainId: string) {
    super(
      `Domain not found: ${domainId}`,
      {
        ux: [
          'The specified domain does not exist',
          'Please verify the domain ID and try again',
          'Use listDomains to see available domains'
        ],
        dx: [
          `Domain ID: ${domainId}`,
          'Check if domain exists in ddd__domains collection',
          'Verify MongoDB connection to domain designer database',
          'Domain may have been deleted or never created'
        ]
      }
    );
  }
}

export class BoundedContextNotFoundError extends BaseDomainError {
  readonly code = 'BOUNDED_CONTEXT_NOT_FOUND';
  
  constructor(contextId: string) {
    super(
      `Bounded context not found: ${contextId}`,
      {
        ux: [
          'The specified bounded context does not exist',
          'Please verify the context ID and try again',
          'Use listBoundedContexts to see available contexts'
        ],
        dx: [
          `Context ID: ${contextId}`,
          'Check if context exists in ddd__boundedContexts collection',
          'Verify relationship with parent domain',
          'Context may have been deleted or never created'
        ]
      }
    );
  }
}

export class AggregateNotFoundError extends BaseDomainError {
  readonly code = 'AGGREGATE_NOT_FOUND';
  
  constructor(aggregateId: string) {
    super(
      `Aggregate not found: ${aggregateId}`,
      {
        ux: [
          'The specified aggregate does not exist',
          'Please verify the aggregate ID and try again',
          'Use listAggregates to see available aggregates'
        ],
        dx: [
          `Aggregate ID: ${aggregateId}`,
          'Check if aggregate exists in ddd__aggregates collection',
          'Verify relationship with parent bounded context',
          'Aggregate may have been deleted or never created'
        ]
      }
    );
  }
}

/**
 * Application errors for Domain Designer operations
 */

export class DomainServiceNotAvailableError extends BaseApplicationError {
  readonly code = 'DOMAIN_SERVICE_NOT_AVAILABLE';
  
  static queryServiceNotFound(serviceName: string, methodName: string): DomainServiceNotAvailableError {
    return new DomainServiceNotAvailableError(
      `Domain service method not found: ${serviceName}.${methodName}`,
      {
        ux: [
          'Domain modeling service is temporarily unavailable',
          'Please try again in a moment',
          'Contact support if the problem persists'
        ],
        dx: [
          `Service: ${serviceName}`,
          `Method: ${methodName}`,
          'Check if correct query service is imported from @swoft/ddd',
          'Verify service exports in package index.ts',
          'Consider using DomainQueryService instead of DomainCQRSQueryService',
          'Check for breaking changes in @swoft/ddd package'
        ]
      }
    );
  }

  static serviceInitializationFailed(serviceName: string, cause: Error): DomainServiceNotAvailableError {
    return new DomainServiceNotAvailableError(
      `Failed to initialize domain service: ${serviceName}`,
      {
        ux: [
          'Domain modeling service could not be started',
          'Please check system configuration',
          'Contact your administrator for assistance'
        ],
        dx: [
          `Service: ${serviceName}`,
          `Cause: ${cause.message}`,
          'Check MongoDB connection configuration',
          'Verify repository dependencies are properly injected',
          'Check for circular dependencies in service initialization'
        ]
      }
    );
  }
}

export class DomainOperationError extends BaseApplicationError {
  readonly code = 'DOMAIN_OPERATION_ERROR';
  
  static createFailed(entityType: string, reason: string): DomainOperationError {
    return new DomainOperationError(
      `Failed to create ${entityType}: ${reason}`,
      {
        ux: [
          `Unable to create new ${entityType}`,
          'Please verify all required fields are provided',
          'Check that parent entities exist and are accessible'
        ],
        dx: [
          `Entity type: ${entityType}`,
          `Reason: ${reason}`,
          'Verify payload matches entity schema requirements',
          'Check for unique constraint violations',
          'Ensure parent entity relationships are valid'
        ]
      }
    );
  }

  static updateFailed(entityType: string, id: string, reason: string): DomainOperationError {
    return new DomainOperationError(
      `Failed to update ${entityType} ${id}: ${reason}`,
      {
        ux: [
          `Unable to update ${entityType}`,
          'Please verify the entity exists and is not locked',
          'Check that you have permission to modify this entity'
        ],
        dx: [
          `Entity type: ${entityType}`,
          `Entity ID: ${id}`,
          `Reason: ${reason}`,
          'Check for concurrent modification conflicts',
          'Verify update payload schema',
          'Ensure business invariants are not violated'
        ]
      }
    );
  }
}

/**
 * Infrastructure errors for Domain Designer operations
 */

export class DomainRepositoryError extends BaseInfrastructureError {
  readonly code = 'DOMAIN_REPOSITORY_ERROR';
  
  static connectionFailed(operation: string, cause: Error): DomainRepositoryError {
    return new DomainRepositoryError(
      `Domain repository ${operation} failed`,
      {
        ux: [
          'Unable to access domain modeling data',
          'Please try again in a moment',
          'Contact support if the problem persists'
        ],
        dx: [
          `Operation: ${operation}`,
          `Cause: ${cause.message}`,
          'Check MongoDB connection to domain designer database',
          'Verify database credentials and permissions',
          'Check network connectivity to MongoDB cluster'
        ]
      }
    );
  }

  static queryFailed(collection: string, query: string, cause: Error): DomainRepositoryError {
    return new DomainRepositoryError(
      `Failed to query ${collection}`,
      {
        ux: [
          'Unable to retrieve domain modeling data',
          'The query could not be completed',
          'Please try a different search or contact support'
        ],
        dx: [
          `Collection: ${collection}`,
          `Query: ${query}`,
          `Cause: ${cause.message}`,
          'Check MongoDB query syntax',
          'Verify collection indexes are properly created',
          'Check for query timeout or resource limits'
        ]
      }
    );
  }
}

